// Libraries
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
// Hooks
import useEventListener from 'src/hooks/useEventListener';
// Global Actions/Constants
import { loadPage } from 'src/actions/common';
import { DEFAULT_LAYOUT } from 'src/constants/common';
import { isDevelopment } from 'src/utils/env';
// HOC
import withLayout from 'src/hoc/withLayout';
// Component Actions/Constants
import { SPEEDTEST_IFRAME_URL, PAGE_NAME, DEV_TEST_DATA } from './constants';
import { speedtestResultsReceived } from './actions';
// Components
// eslint-disable-next-line import/order
import Button, { THEMES as BUTTON_THEMES } from 'HTKit/Forms/Button';
import SpeedTestFAQ from './SpeedTestFAQ';
import ProductCards from './ProductCards';
// Styles
import styles from './styles.scss';

const formatSpeeds = ({ type, units }) => {
  // https://support.ookla.com/hc/en-us/articles/115005319507-Passing-Test-Results-To-The-Browser
  // Per docs the units will always return as 'Kbps'
  if (!units) return type;
  if (units.toLowerCase() === 'kbps') return Number(type) / 1000;
  return type;
};

const SpeedTestPage = ({ isSpeedtestComplete, skus }) => {
  const dispatch = useDispatch();
  const handleMessageEvent = (event) => {
    const validOrigin = event.origin === SPEEDTEST_IFRAME_URL;
    const { data: { upload, download, latency, units } = {} } = event;
    const isCompleteResponse = validOrigin && [upload, download, latency].every((f) => f);
    if (!isCompleteResponse) return null;
    const results = {
      ping: latency.minimum,
      jitter: latency.jitter,
      upload: formatSpeeds({ type: upload, units }),
      download: formatSpeeds({ type: download, units }),
    };
    return dispatch(speedtestResultsReceived(results));
  };

  const handleTestData = () => {
    // Since the test won't run on localhost, we need a way to test the test results being saved in DB
    const { ping, jitter, upload, download } = DEV_TEST_DATA;
    const results = {
      ping,
      jitter,
      upload: formatSpeeds({ type: upload, units: 'Kbps' }),
      download: formatSpeeds({ type: download, units: 'Kbps' }),
    };
    return dispatch(speedtestResultsReceived(results));
  };

  useEffect(() => {
    dispatch(loadPage(PAGE_NAME));
  }, []);

  useEventListener({ eventName: 'message', handler: handleMessageEvent });

  return (
    <article className="site-v2">
      <iframe width="100%" frameBorder="0" height="640px" src={SPEEDTEST_IFRAME_URL} />
      <div className={styles.extrasContainer}>
        {isDevelopment() && (
          <Button theme={BUTTON_THEMES.V2PRIMARY} onClick={handleTestData}>
            Send Fake Test Data
          </Button>
        )}
        {isSpeedtestComplete && <ProductCards skus={skus} />}
        <SpeedTestFAQ />
      </div>
    </article>
  );
};

SpeedTestPage.propTypes = {
  isSpeedtestComplete: PropTypes.bool,
  skus: PropTypes.array.isRequired,
};

SpeedTestPage.defaultProps = {
  skus: [],
};

export default React.memo(withLayout(DEFAULT_LAYOUT)(SpeedTestPage));
